import { Command } from 'commander';
import chalk from 'chalk';
import { getApiKey } from '../lib/config.js';
import { OutputFormatter, getJsonFlag, createSpinner } from '../lib/output.js';
import { handleError, AuthenticationError } from '../lib/errors.js';
import { getSDKClient } from '../lib/sdk-client.js';

const PROVIDERS_URL = 'https://app.mutagent.io/settings/providers';

export function createUsageCommand(): Command {
  const usage = new Command('usage')
    .description('Show resource counts (prompts, datasets, evaluations, optimizations, experiments)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent usage
  ${chalk.dim('$')} mutagent usage --json
    `);

  usage.action(async () => {
    const isJson = getJsonFlag(usage);
    const output = new OutputFormatter(isJson ? 'json' : 'table');

    try {
      const apiKey = getApiKey();
      if (!apiKey) {
        throw new AuthenticationError();
      }

      const spinner = await createSpinner('Fetching usage data...', isJson).start();

      const client = await getSDKClient();

      // Fetch prompts
      const prompts = await client.listPrompts();
      const promptCount = prompts.length;

      // Fetch datasets and evaluations across all prompts in parallel
      let datasetCount = 0;
      let evaluationCount = 0;

      if (promptCount > 0) {
        const results = await Promise.all(
          prompts.map(async (prompt) => {
            const id = String(prompt.id);
            const [datasets, evaluations] = await Promise.all([
              client.listDatasets(id).catch(() => [] as unknown[]),
              client.listEvaluations(id).catch(() => [] as unknown[]),
            ]);
            return { datasets: datasets.length, evaluations: evaluations.length };
          })
        );

        for (const r of results) {
          datasetCount += r.datasets;
          evaluationCount += r.evaluations;
        }
      }

      // Fetch optimizations and experiments counts
      const [optimizations, experiments] = await Promise.all([
        client.listOptimizations().catch(() => [] as unknown[]),
        client.listExperiments().catch(() => [] as unknown[]),
      ]);
      const optimizationCount = Array.isArray(optimizations) ? optimizations.length : 0;
      const experimentCount = Array.isArray(experiments) ? experiments.length : 0;

      if (spinner && typeof (spinner as { stop?: () => void }).stop === 'function') {
        (spinner as { stop: () => void }).stop();
      }

      if (isJson) {
        output.output({
          resources: {
            prompts: promptCount,
            datasets: datasetCount,
            evaluations: evaluationCount,
            optimizations: optimizationCount,
            experiments: experimentCount,
          },
          _links: {
            providers: PROVIDERS_URL,
            dashboard: 'https://app.mutagent.io',
          },
        });
      } else {
        console.log('');
        console.log(chalk.bold('\ud83d\udcca MutagenT Usage'));
        console.log(chalk.dim('\u2500'.repeat(45)));
        console.log('');
        console.log(chalk.bold('Resources:'));
        console.log(`  Prompts:         ${chalk.cyan(String(promptCount))}`);
        console.log(`  Datasets:        ${chalk.cyan(String(datasetCount))}`);
        console.log(`  Evaluations:     ${chalk.cyan(String(evaluationCount))}`);
        console.log(`  Optimizations:   ${chalk.cyan(String(optimizationCount))}`);
        console.log(`  Experiments:     ${chalk.cyan(String(experimentCount))}`);
        console.log('');
        console.log(`  Providers: ${chalk.underline(PROVIDERS_URL)}`);
        console.log('');
      }
    } catch (error) {
      handleError(error, isJson);
    }
  });

  return usage;
}
